import {Injectable, Optional} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {ErrorReporter} from '../shared/error-reporting/error-reporter';

/**
 * Distinct schema bootstrap phases (AC-35).
 * Authenticated shell should render only when status === 'loaded'.
 */
export type SchemaBootstrapStatus =
  | 'idle'
  | 'no-token'
  | 'loading'
  | 'loaded'
  | 'unauthorized'
  | 'unavailable'
  | 'malformed-schema';

export interface SchemaBootstrapState {
  status: SchemaBootstrapStatus;
  error: unknown | null;
  retryCount: number;
  /** True when a fetch is in flight (prevents duplicate requests). */
  inFlight: boolean;
}

export const SCHEMA_BOOTSTRAP_MAX_RETRIES = 3;

const initialState = (): SchemaBootstrapState => ({
  status: 'idle',
  error: null,
  retryCount: 0,
  inFlight: false,
});

@Injectable({providedIn: 'root'})
export class SchemaBootstrapService {

  private readonly stateSubject = new BehaviorSubject<SchemaBootstrapState>(initialState());

  readonly state$: Observable<SchemaBootstrapState> = this.stateSubject.asObservable();
  readonly status$: Observable<SchemaBootstrapStatus> = this.state$.pipe(
    map(s => s.status),
    distinctUntilChanged(),
  );

  constructor(@Optional() private errorReporter?: ErrorReporter) {
  }

  get state(): SchemaBootstrapState {
    return this.stateSubject.value;
  }

  get status(): SchemaBootstrapStatus {
    return this.stateSubject.value.status;
  }

  get canRetry(): boolean {
    const s = this.stateSubject.value;
    return !s.inFlight
      && s.retryCount < SCHEMA_BOOTSTRAP_MAX_RETRIES
      && (s.status === 'unavailable' || s.status === 'malformed-schema');
  }

  get isBlockingFailure(): boolean {
    const st = this.status;
    return st === 'unavailable' || st === 'malformed-schema' || st === 'unauthorized';
  }

  markNoToken(): void {
    this.stateSubject.next({
      status: 'no-token',
      error: null,
      retryCount: 0,
      inFlight: false,
    });
  }

  beginLoad(): boolean {
    const cur = this.stateSubject.value;
    if (cur.inFlight) {
      return false;
    }
    this.stateSubject.next({
      ...cur,
      status: 'loading',
      error: null,
      inFlight: true,
    });
    return true;
  }

  markLoaded(): void {
    this.stateSubject.next({
      status: 'loaded',
      error: null,
      retryCount: 0,
      inFlight: false,
    });
  }

  markUnauthorized(error?: unknown): void {
    this.reportFailure('unauthorized', error);
    this.stateSubject.next({
      status: 'unauthorized',
      error: error ?? null,
      retryCount: this.stateSubject.value.retryCount,
      inFlight: false,
    });
  }

  markUnavailable(error: unknown): void {
    this.reportFailure('unavailable', error);
    const cur = this.stateSubject.value;
    this.stateSubject.next({
      status: 'unavailable',
      error,
      retryCount: cur.retryCount,
      inFlight: false,
    });
  }

  markMalformed(error: unknown): void {
    this.reportFailure('malformed-schema', error);
    const cur = this.stateSubject.value;
    this.stateSubject.next({
      status: 'malformed-schema',
      error,
      retryCount: cur.retryCount,
      inFlight: false,
    });
  }

  /** Increment retry counter; returns false if max retries reached or already in flight. */
  beginRetry(): boolean {
    const cur = this.stateSubject.value;
    if (cur.inFlight || cur.retryCount >= SCHEMA_BOOTSTRAP_MAX_RETRIES) {
      return false;
    }
    this.stateSubject.next({
      status: 'loading',
      error: null,
      retryCount: cur.retryCount + 1,
      inFlight: true,
    });
    return true;
  }

  reset(): void {
    this.stateSubject.next(initialState());
  }

  private reportFailure(kind: string, error: unknown): void {
    this.errorReporter?.report(error ?? new Error(`schema-bootstrap:${kind}`), {
      schemaBootstrap: kind,
      retryCount: this.stateSubject.value.retryCount,
    });
  }
}
